import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIMessageResponse } from '@/shared/services/apiConfig';
import { User } from '@/shared/types/userTypes';
import { SingleResponse } from '@/shared/types/responseTypes';
import type { MfaFactor, RegisterUserData, UserLoginData } from '@/shared/types/authTypes';

export const fetchCurrentUser = async (): Promise<User> => {
  const response = await apiClient.get<SingleResponse<User>>('/api/v1/auth/me');
  return response.data;
};

export const logout = async (): Promise<void> => {
  try {
    await apiClient.post<APIMessageResponse>('/api/v1/auth/logout');
  } finally {
    // Expire portal cookies even when /logout 401s (stale cookie from another env).
    apiClient.clearAuth();
  }
};

// User Login Mutation
export const useUserLogin = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ email, password }: { email: string, password: string }) => {
      const response = await apiClient.post<SingleResponse<UserLoginData>>(
        `/api/v1/auth/login`,
        {
          body: { email, password }
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-login'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// User registration mutation
export const useRegisterUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userData: RegisterUserData) => {
      return await apiClient.post(
        '/api/v1/auth/register',
        {
          body: userData
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-registered'] });
    },
    onError: (error) => {
      return error;
    },
  });
};

// Forgot Password Mutation
export const useForgotPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (email: string) => {
      return await apiClient.post<APIMessageResponse>(
        '/api/v1/auth/forgot-password',
        {
          body: { email }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-forgot-password'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

//Validate Token
export const useValidateToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      return await apiClient.get<APIMessageResponse>(
        '/api/v1/auth/validate-reset-token',
        {
          params: { token }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-validate-token'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Reset Password Mutation
export const useResetPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ token, newPassword }: { token: string, newPassword: string }) => {
      return await apiClient.post<APIMessageResponse>(
        '/api/v1/auth/reset-password',
        {
          body: { token, newPassword }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-reset-password'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Verify OTP Mutation — cookie is set by Set-Cookie; profile comes from /me
export const useVerifyOTP = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ authId, code, factor }: { authId: string; code: string; factor: MfaFactor }) => {
      await apiClient.post(
        '/api/v1/auth/verify',
        {
          body: { authId, code, factor },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-verified-otp'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};

// Resend OTP Mutation
export const useSendOTP = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ authId }: { authId: string }) => {
      const response = await apiClient.post<SingleResponse<UserLoginData>>(
        '/api/v1/auth/send-otp',
        {
          body: { authId }
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-send-otp'] });
    },
    onError: (error: Error) => {
      return error;
    },
  });
};